import {
	AfterContentChecked,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from "@angular/core";
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, lastValueFrom } from "rxjs";
import { CebeViewModel } from "src/app/_core/models/centros/cebe.model";
import {
	CentroActividadViewModel,
	LocalizacionViewModel,
	TipoInstalacionViewModel,
} from "src/app/_core/models/centros/centro-list-base.model";
import { CentrosViewModel } from "src/app/_core/models/centros/centro.model";
import { PaisViewModel } from "src/app/_core/models/centros/pais.model";
import { CebesService } from "src/app/_core/providers/cebes.service";
import { CentrosService } from "src/app/_core/providers/centros.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { SettingsService } from "src/app/_core/providers/settings.service";
import { CentroPageComponent } from "../../../pages/centro/centro.page";

@Component({
    selector: "app-centro-details-card",
    templateUrl: "./centro-details-card.component.html",
    styleUrls: ["./centro-details-card.component.scss"],
    standalone: false
})
export class CentroDetailsCardComponent implements OnInit, AfterContentChecked {
	@Input() centro: CentrosViewModel;
	@Input() parent: CentroPageComponent;

	@Output() detailFormComplete = new EventEmitter<boolean>();
	@Output() centroDetailData = new EventEmitter<FormGroup>();

	public alltipoInstalacionList: TipoInstalacionViewModel[] = [];
	public tipoInstalacionList: TipoInstalacionViewModel[] = [];
	public centroActividadList: CentroActividadViewModel[] = [];
	public localizacionList: LocalizacionViewModel[] = [];
	public paisList: PaisViewModel[] = [];
	// ISO-2 del pais del centro (heredado del CeBe primario o del contrato padre); filtra el arbol de Ubicacion.
	public codigoPaisCentro: string | null = null;

	isAdmin: boolean = false;
	cebe: CebeViewModel; // CeBe "primario" (primero seleccionado): rige país/provincia.
	cebes: CebeViewModel[] = []; // CeBe actualmente vinculados al centro.
	cebesContrato: CebeViewModel[] = []; // CeBe habilitados en el contrato (candidatos).

	myDetailForm: FormGroup;
	countControls: number = 0;
	countFilled: number = 0;
	imagenPreview: string | ArrayBuffer | null = null;
	imagenFile: File | null = null;

	constructor(
		private readonly _masterService: MasterService,
		private readonly _centrosService: CentrosService,
		private readonly _cebesService: CebesService,
		private readonly _notificationService: NotificationService,
		private readonly _translateService: TranslateService,
		public translate: TranslateService,
		private readonly _settingsService: SettingsService,
		private readonly fb: FormBuilder
	) {}

	// Events
	async ngOnInit(): Promise<void> {
		this._settingsService.isAdmin().subscribe((result) => {
			this.isAdmin = result;
		});

		this.loadCentroDetailsFormGroup();

		// Multi-CeBe: candidatos = CeBe del contrato (List-gated, sin ver el contrato);
		// seleccionados = CeBe vinculados hoy al centro. El "primario" (primero) rige país/provincia.
		const contratoId = this.parent?.cebeCentro?.contratoID ?? null;
		const [candidatos, actuales] = await Promise.all([
			contratoId != null
				? lastValueFrom(this._cebesService.getCebesDeContratoLookup(contratoId))
				: Promise.resolve([] as CebeViewModel[]),
			this.centro?.id
				? lastValueFrom(this._cebesService.getCebesDeCentro(this.centro.id))
				: Promise.resolve([] as CebeViewModel[]),
		]);
		this.cebesContrato = candidatos ?? [];
		this.cebes = actuales ?? [];
		this.cebe = this.cebes[0] ?? null;
		this.myDetailForm.get("cebeIds").setValue(this.cebes.map((c) => c.id));

		this.aplicarValidadoresPais();
		this._loadData();

		this.parent.loadCebesCentro();
	}

	/**
	 * País efectivo del centro: CeBe primario si lo hay; si no (centro colgando directo del
	 * contrato, sin CeBe vinculado), el país del CONTRATO padre (lookup de contexto).
	 */
	get paisIdCentro(): number | null {
		return this.cebe?.paisID ?? this.parent?.contratoPadre?.paisId ?? null;
	}

	/** España = Pais.ID 65 (id interno, no ISO2). Rige el campo Provincia/Región. */
	get esEspana(): boolean {
		return this.paisIdCentro === 65;
	}

	/**
	 * Geografía (Ubicación) es el campo obligatorio; provincia (localizacionID) y ciudad pasan a
	 * opcionales (se alimentan por detrás en el servidor). Recalcula además el ISO-2 del país del
	 * centro para filtrar el árbol. Se re-aplica si cambian los CeBe vinculados o cuando el contrato
	 * padre (fallback de país) llega en diferido. Ver ADR-0010.
	 */
	aplicarValidadoresPais(): void {
		if (!this.myDetailForm) {
			return;
		}
		this.recomputeCodigoPais();
		this.myDetailForm.get("geografiaID")?.setValidators(Validators.required);
		this.myDetailForm.get("ciudad")?.removeValidators(Validators.required);
		this.myDetailForm.get("localizacionID")?.removeValidators(Validators.required);
		this.myDetailForm.get("geografiaID")?.updateValueAndValidity();
		this.myDetailForm.get("ciudad")?.updateValueAndValidity();
		this.myDetailForm.get("localizacionID")?.updateValueAndValidity();
	}

	/** Resuelve el ISO-2 del país del centro a partir del Pais.ID interno (vía maestro de países). */
	private recomputeCodigoPais(): void {
		const id = this.paisIdCentro;
		this.codigoPaisCentro = id
			? (this.paisList?.find((p) => p.id === id)?.codigoIso2 ?? null)
			: null;
	}

	ngAfterContentChecked(): void {
		this.getFormStatus();
	}

	loadCentroDetailsFormGroup() {
		this.myDetailForm = this.fb.group({
			descripcion: [this.centro?.descripcion, [Validators.required]],
			tipoInstalacionID: [
				this.centro?.tipoInstalacionID,
				[Validators.required],
			],
			latitud: [this.centro?.latitud ?? null, [Validators.required]],
			longitud: [this.centro?.longitud ?? null, [Validators.required]],
			ciudad: [this.centro?.ciudad],
			geografiaID: [this.centro?.geografiaID, [Validators.required]],
			centroActividadID: [this.centro?.centroActividadID, []],
			localizacionID: [this.centro?.localizacionID],
			activo: [this.centro?.activo ? this.centro?.activo : false],
			imageDataB64: [this.centro?.imageDataB64],
			cebeIds: [[]],
		});

		this.centroDetailData.emit(this.myDetailForm);

		this.myDetailForm
			.get("tipoInstalacionID")
			.valueChanges.subscribe((s) => {
				this.parent.showHideInstallationFields();
			});

		// Al cambiar los CeBe vinculados: recalcular el "primario" (país/provincia) y la lista de
		// tipos de instalación disponibles (unión de ternas de los CeBe seleccionados + el ya elegido).
		this.myDetailForm.get("cebeIds").valueChanges.subscribe(() => {
			const seleccion = this.cebesSeleccionados();
			this.cebe = seleccion[0] ?? null;
			this.recomputeTipoInstalacionList();
			this.aplicarValidadoresPais();
		});

		this.myDetailForm.valueChanges.subscribe(() => {
			this.getFormStatus();

			this.centroDetailData.emit(this.myDetailForm);
		});
	}

	get f(): { [key: string]: AbstractControl } {
		return this.myDetailForm.controls;
	}

	// ── Modo lectura / edición (gobernado por la página) ─────────────────────
	get editing(): boolean {
		return !!(this.parent?.isEditing || this.parent?.isCreating);
	}

	// Etiquetas de opción para los app-ds-select (maestros con clave i18n).
	tipoInstalacionLabelOf = (item: TipoInstalacionViewModel): string =>
		this._translateService.instant("centro.installations." + item.codigo);
	centroActividadLabelOf = (item: CentroActividadViewModel): string =>
		this._translateService.instant("centro.centerActivities." + item.codigo);
	localizacionLabelOf = (item: LocalizacionViewModel): string =>
		item?.descripcion ?? "";

	// Etiquetas de visualización (modo lectura) resueltas desde los maestros.
	get estadoActivo(): boolean {
		return !!this.myDetailForm?.get("activo")?.value;
	}
	get descripcionVal(): string {
		return this.myDetailForm?.get("descripcion")?.value || "—";
	}
	get tipoInstalacionLabel(): string {
		const id = this.myDetailForm?.get("tipoInstalacionID")?.value;
		const it =
			this.tipoInstalacionList?.find((x) => x.id === id) ||
			this.alltipoInstalacionList?.find((x) => x.id === id);
		return it
			? this._translateService.instant("centro.installations." + it.codigo)
			: "—";
	}
	get centroActividadLabel(): string {
		const id = this.myDetailForm?.get("centroActividadID")?.value;
		if (!id) {
			return "—";
		}
		const it = this.centroActividadList?.find((x) => x.id === id);
		return it
			? this._translateService.instant(
					"centro.centerActivities." + it.codigo,
				)
			: "—";
	}
	get provinciaLabel(): string {
		if (this.esEspana) {
			const id = this.myDetailForm?.get("localizacionID")?.value;
			const it = this.localizacionList?.find((x) => x.id === id);
			return it?.descripcion || "—";
		}
		return this.myDetailForm?.get("ciudad")?.value || "—";
	}

	/** Etiqueta de Ubicación (modo lectura): ruta legible sin el continente. */
	get ubicacionLabel(): string {
		const p = this.centro?.geografiaPath;
		if (p) {
			const parts = p.split("/");
			return (parts.length > 1 ? parts.slice(1) : parts).join(" / ");
		}
		return this.centro?.geografiaDescripcion || "—";
	}
	get cebeLabel(): string {
		return this.cebe?.descripcion || "—";
	}
	/** Etiqueta de los CeBe vinculados (modo lectura): "codigo — descripcion, …" o "—". */
	get cebesLabel(): string {
		const sel = this.cebesSeleccionados();
		if (!sel.length) {
			return "—";
		}
		return sel
			.map((c) => (c.descripcion ? `${c.codigo} — ${c.descripcion}` : c.codigo))
			.join(", ");
	}
	get responsableLabel(): string {
		const v: any = this.parent?.responsableControl?.value;
		const name = v && typeof v === "object" ? v.nombre : null;
		return name || (this.centro as any)?.responsable || "—";
	}

	getFormStatus() {
		this.countControls = 0;
		this.countFilled = 0;

		Object.keys(this.myDetailForm.controls).forEach((controlName) => {
			const control = this.myDetailForm.get(controlName);

			const controlElement = document.querySelector(
				`[formControlName="${controlName}"]`
			);

			if (controlElement) {
				const hasHiddenClass =
					controlElement.parentElement.classList.contains("hidden");

				if (!hasHiddenClass) {
					this.countControls++;

					if (control?.value !== null && control?.value !== "") {
						this.countFilled++;
					}
				}
			}
		});

		if (this.countFilled !== 0) {
			this.detailFormComplete.emit(
				this.countControls === this.countFilled
			);
		}
	}

	private _loadData(): void {
		forkJoin([
			this._masterService.getTipoInstalacionList(),
			this._masterService.getCentroActividadList(),
			this._masterService.getLocalizacionList(),
			this._masterService.getPaises(),
		]).subscribe(([s1, s2, s3, s4]) => {
			this.alltipoInstalacionList = s1;
			this.centroActividadList = s2;
			this.localizacionList = s3;
			this.paisList = s4;
			// El país (ISO-2) puede haber llegado después del primer aplicarValidadoresPais.
			this.recomputeCodigoPais();

			this.recomputeTipoInstalacionList();

			this.parent.hideAllOperacionDisenoFields();
			this.parent.initFieldsAndActivitySelector();
		});
		this.imagenPreview = this.centro?.imageDataB64;
	}

	/** CeBe actualmente seleccionados en el form (resueltos contra candidatos del contrato + actuales). */
	private cebesSeleccionados(): CebeViewModel[] {
		const ids: number[] = this.myDetailForm?.get("cebeIds")?.value ?? [];
		const pool = [...(this.cebesContrato ?? []), ...(this.cebes ?? [])];
		return ids
			.map((id) => pool.find((c) => c.id === id))
			.filter((c): c is CebeViewModel => !!c);
	}

	/**
	 * Tipos de instalación disponibles = UNIÓN de las ternas (TipoProyecto/Sociedad/Actividad) de
	 * los CeBe seleccionados. Además SIEMPRE se incluye el tipo ya guardado en el centro (aunque su
	 * terna no aplique), porque es una selección ya realizada que no debe desaparecer.
	 */
	private recomputeTipoInstalacionList(): void {
		const seleccion = this.cebesSeleccionados();
		const ternas = new Set(
			seleccion.map(
				(c) => `${c.tipoProyectoID}|${c.sociedadID}|${c.actividadID}`,
			),
		);
		const storedId = this.myDetailForm?.get("tipoInstalacionID")?.value;
		this.tipoInstalacionList = (this.alltipoInstalacionList ?? []).filter(
			(inst) => {
				const terna = `${inst.tipoProyectoID}|${inst.sociedadID}|${inst.actividadID}`;
				return ternas.has(terna) || (storedId != null && inst.id === storedId);
			},
		);
	}

	/** Opciones del selector de CeBe: candidatos del contrato ∪ vinculados hoy (dedup).
	 * Cubre el caso raro de un CeBe vinculado que ya no figure entre los candidatos. */
	get cebesOpciones(): CebeViewModel[] {
		const vistos = new Set<number>();
		return [...(this.cebesContrato ?? []), ...(this.cebes ?? [])].filter(
			(c) => !vistos.has(c.id) && !!vistos.add(c.id),
		);
	}

	isCebeSelected(id: number): boolean {
		const ids: number[] = this.myDetailForm?.get("cebeIds")?.value ?? [];
		return ids.includes(id);
	}

	/** Alta/baja de un CeBe vía chips. setValue dispara la suscripción existente
	 * (recalcula primario, tipos de instalación y validadores de país). */
	toggleCebe(id: number): void {
		const ctrl = this.myDetailForm?.get("cebeIds");
		if (!ctrl) {
			return;
		}
		const ids: number[] = ctrl.value ?? [];
		ctrl.setValue(
			ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
		);
		ctrl.markAsDirty();
	}

	/** ¿El contrato del centro tiene CeBe habilitados? (si no, hay que crearlos en el contrato). */
	get contratoSinCebes(): boolean {
		return !this.cebesContrato?.length;
	}

	/** ¿El centro tiene algún CeBe vinculado? */
	get sinCebesVinculados(): boolean {
		return !(this.myDetailForm?.get("cebeIds")?.value?.length);
	}

	/** Copia un código al portapapeles (mismo patrón que el detalle de contrato). */
	async copyCodigo(value?: string | null): Promise<void> {
		if (!value) {
			return;
		}
		try {
			await navigator.clipboard.writeText(value);
			this._notificationService.showInfo(
				this._translateService.instant("contracts.codes.copied"),
			);
		} catch {
			// Portapapeles no disponible (permisos/contexto no seguro): sin ruido.
		}
	}

	Migrar() {
		this._centrosService.Migrar(this.centro?.id).subscribe((s) => {
			this._notificationService.showSuccess(
				this._translateService.instant("messages.savedSuccessfully"),
				null
			);
			this.parent.ngOnInit();
		});
	}

	Validate() {
		this._centrosService.Validate(this.centro?.id).subscribe((s) => {
			this._notificationService.showSuccess(
				this._translateService.instant("messages.savedSuccessfully"),
				null
			);
			this.parent.ngOnInit();
		});
	}

	onFileChange(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files && input.files[0];
		const fileSizeKb = file.size / 1024;

		if (
			file &&
			(file.type === "image/jpeg" || file.type === "image/png") &&
			fileSizeKb <= 1000
		) {
			this.imagenFile = file;

			const reader = new FileReader();
			reader.onload = () => {
				const base64Image = reader.result as string;
				this.myDetailForm.patchValue({ imageDataB64: base64Image });
				this.myDetailForm.get("imageDataB64")?.updateValueAndValidity();
				this.imagenPreview = base64Image;
			};

			reader.readAsDataURL(file);
		} else {
			this.myDetailForm.patchValue({ imageDataB64: null });
			this.imagenFile = null;
			this.imagenPreview = null;
			input.value = "";
			if (fileSizeKb > 1000) {
				this._notificationService.showError(
					this._translateService.instant("messages.imageRequiredType")
				);
			}
		}
	}
}
